import { formatDate, parseDate } from "@wellsky/atlas-ui-react";
import type { DateRange } from "@wellsky/atlas-ui-react";
import {
  convertTimeRangeToDateRange,
  convertCustomDateRangeToFilter,
} from "./dashboardUtils";

// Atlas Calendar uses "YYYY-MM-DD" tokens for parse/format. CalendarDate.month
// is 0-indexed; parseDate/formatDate handle that conversion internally.
const WIRE_FORMAT = "YYYY-MM-DD";

/**
 * Convert a dueDate filter value into an Atlas DateRange for seeding the
 * calendar's range highlight. Handles the polymorphic value: a "start,end"
 * range (custom / default, either side may be empty), a preset label, the
 * "no_due_date" sentinel, or an empty string.
 */
export function valueToCalendarRange(value: string): DateRange {
  const v = (value ?? "").trim();
  if (!v || v === "no_due_date") return { start: null, end: null };

  let rangeStr = v;
  if (!v.includes(",")) {
    // Preset label -> resolve to a concrete range first.
    const resolved = convertTimeRangeToDateRange(v);
    rangeStr = resolved ? `${resolved.startDate},${resolved.endDate}` : ",";
  }

  const [rawStart, rawEnd] = rangeStr.split(",");
  return {
    start: rawStart?.trim() ? parseDate(rawStart.trim(), WIRE_FORMAT) : null,
    end: rawEnd?.trim() ? parseDate(rawEnd.trim(), WIRE_FORMAT) : null,
  };
}

/**
 * Convert an Atlas DateRange back into the "start,end" wire string. Empty
 * endpoints map to open-ended ranges (",end" / "start,"); a fully empty range
 * maps to "" (no filter).
 */
export function dateRangeToRangeString(range: DateRange): string {
  const start = range.start ? formatDate(range.start, WIRE_FORMAT) : "";
  const end = range.end ? formatDate(range.end, WIRE_FORMAT) : "";
  return convertCustomDateRangeToFilter(start || null, end || null) ?? "";
}

/** True when both endpoints are set and start is after end (custom-range guard). */
export function isDateRangeInvalid(range: DateRange): boolean {
  const { start, end } = range;
  if (!start || !end) return false;
  return formatDate(start, WIRE_FORMAT) > formatDate(end, WIRE_FORMAT);
}
